import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useRolesPermissions } from "@src/hooks/useRolesPermission";
import type { MenuProps } from "antd";
import { Menu } from "antd";
import Button from "../button/Button";
import { UserRoleEnum } from "@src/types";
import CloseIcon from "../../assets/icons/close.svg?react";
import Logo from "./logo/Logo";
import MenuLink from "./link/MenuLink";
import SubMenuLink from "./link/SubMenuLink";
import FooterMenuLink from "./link/FooterMenuLink";
import {
  IconDashboard,
  IconGettingStarted,
  IconAdminTop,
  IconUsers,
  IconWellbeingFoundation,
  IconEducatorOnboarding,
  IconSchoolOnboarding,
  IconDistrictOnboarding,
  IconQuickstartArticles,
  IconScopeSequence,
  IconCurriculumMapping,
  IconLessonRoadmaps,
  IconKickOffCall,
  IconUserManage,
  IconInviteStaff,
  IconViewUsage,
  IconAccounts,
  IconReportTop,
  IconSentryTracking,
  IconCourses,
  IconUnits,
  IconGroups,
  IconMyCollection,
  IconHeUsers,
  IconHeLinks,
  IconStudentIntroduction,
  IconBrainBreak,
  IconSkillBuilder,
  IconLessons,
  IconStandalone,
  IconLessonLibrary,
  IconPrograms,
  IconAssessmentReports,
  IconPreProgramQuiz,
  IconSummaryReports,
  IconStudentWellbeing,
  IconProfessionalDevelopment,
  IconParentsCaregivers,
  IconCaregiverIntroduction,
  IconCaregiverResources,
  IconSupport,
  IconLiveChat,
  IconMyAccount,
  IconViewActiveLessons,
  IconLogout,
  ProgramID
} from "@constants";
import styles from "./menuContent.module.scss";
import IconArrowDown from "./assets/arrow-down.svg?react";
import IconArrowUp from "./assets/arrow-up.svg?react";

type MenuContentProps = {
  isAussie?: boolean;
  setShowMenuView: (showMenuView: boolean) => void;
  showCloseButton?: boolean;
  isParentAppCheck: boolean;
  onCloseMenu: () => void;
  inviteUserHandler: () => void;
  openStudentWellBeingUnit: () => void;
  openExitTicketSummary: () => void;
  openIntercom: () => void;
  openScheduleSupportCall?: () => void;
  logout: () => void;
  isAUuser: boolean | undefined;
};

function MenuContent({
  isAussie = false,
  setShowMenuView,
  showCloseButton = true,
  isParentAppCheck,
  onCloseMenu,
  inviteUserHandler,
  openStudentWellBeingUnit,
  openExitTicketSummary,
  openIntercom,
  openScheduleSupportCall,
  logout,
  isAUuser,
}: MenuContentProps) {

  const history = useHistory();
  const isAdmin = useRolesPermissions([
    UserRoleEnum.Admin,
    UserRoleEnum.DistrictAdmin,
    UserRoleEnum.ContentAdmin,
    UserRoleEnum.SuperAdmin,
  ]);
  const isDistrictAdmin = useRolesPermissions([UserRoleEnum.DistrictAdmin]);
  const isContentAdmin = useRolesPermissions([UserRoleEnum.ContentAdmin]);
  const isSuperAdmin = useRolesPermissions([UserRoleEnum.SuperAdmin]);

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const handleOpenChange = useCallback((keys: string[]) => {
    const latestOpenKey = keys.find(key => !openKeys.includes(key));
    if (latestOpenKey) {
      setOpenKeys([latestOpenKey]);
    } else {
      setOpenKeys(keys);
    }
  }, [openKeys]);

  const openDashboard = () => {
    setShowMenuView(false);
    history.push("/dashboard");
  };

  const openEducatorOnboarding = useCallback(() => {
    setShowMenuView(false);
    if (isAussie) {
      history.push(
        `/program/educatoronboarding?pid=${ProgramID.EDUCATORONBOARDINGAU}`
      );
    } else {
      history.push(
        `/program/educatoronboarding?pid=${ProgramID.EDUCATORONBOARDING}`
      );
    }
  }, [history, isAussie]);

  const openSchoolOnboarding = useCallback(() => {
    setShowMenuView(false);
    if (isAussie) {
      history.push("/standalone/school-leader-onboarding-aus");
    } else {
      history.push("/standalone/school-leader-onboarding");
    }
  }, [history, isAussie]);

  const openDistrictOnboarding = useCallback(() => {
    setShowMenuView(false);
    history.push("/standalone/district-leader-onboarding");
  }, [history]);

  const openQuickstartArticles = useCallback(() => {
    setShowMenuView(false);
    window.open(
      "https://help.openparachuteschools.com/en/collections/10400596-quickstart-for-educators",
      "_blank"
    );
  }, []);

  const openScopeSequence = useCallback(() => {
    setShowMenuView(false);
    window.open(
      "https://help.openparachuteschools.com/en/collections/9385363-scope-sequence",
      "_blank"
    );
  }, []);

  const openCurriculumMapping = useCallback(() => {
    setShowMenuView(false);
    window.open(
      "https://help.openparachuteschools.com/en/collections/10539286",
      "_blank"
    );
  }, []);

  const openLessonRoadmaps = useCallback(() => {
    setShowMenuView(false);
    window.open(
      "https://help.openparachuteschools.com/en/collections/10703611-roadmaps",
      "_blank"
    );
  }, []);

  const openScheduleKickOffCall = useCallback(() => {
    setShowMenuView(false);
    window.open(
      "https://calendly.com/openparachute-rikki/kickoff-call",
      "_blank"
    );
  }, []);

  const openUserManagement = useCallback(() => {
    setShowMenuView(false);
    if (isSuperAdmin) {
      history.push("/super/users");
    } else {
      history.push("/admin/users");
    }
  }, [history, isSuperAdmin]);

  const openUsagePage = useCallback(() => {
    setShowMenuView(false);
    if (isSuperAdmin) {
      history.push("/super/usage");
    } else {
      history.push("/admin/usage");
    }
  }, [history, isSuperAdmin]);

  const openAccountsPage = useCallback(() => {
    setShowMenuView(false);
    history.push("/super/accounts");
  }, [history]);

  const openStudentIntroduction = useCallback(() => {
    setShowMenuView(false);
    history.push("/wellbeingfoundations?tab=student-introduction");
  }, [history]);

  const openBrainBreak = useCallback(() => {
    setShowMenuView(false);
    history.push("/wellbeingfoundations?tab=brainbreaks");
  }, [history]);

  const openSkillBuilder = useCallback(() => {
    setShowMenuView(false);
    history.push("/wellbeingfoundations?tab=skillbuilders");
  }, [history]);

  const openLessonLibrary = useCallback(() => {
    setShowMenuView(false);
    history.push("/lessons?tab=library");
  }, [history]);

  const openPrograms = useCallback(() => {
    setShowMenuView(false);
    history.push("/lessons?tab=programs");
  }, [history]);

  const openPreProgramQuiz = useCallback(() => {
    setShowMenuView(false);
    if (isAussie) {
      history.push("/standalone/preprogram-quiz-2");
    } else {
      history.push("/preprogram-quiz");
    }
  }, [history, isAussie]);

  const openPostProgramQuiz = useCallback(() => {
    setShowMenuView(false);
    if (isAussie) {
      history.push("/standalone/postprogram-quiz-2");
    } else {
      history.push("/post-program-quiz");
    }
  }, [history, isAussie]);

  const openAssessmentsStudyguides = useCallback(() => {
    setShowMenuView(false);
    history.push("/assessments?tab=1");
  }, [history]);

  const openProfessionalDevelopment = useCallback(() => {
    setShowMenuView(false);
    if (isAussie) {
      history.push(
        `/program/professionaldevelopment?pid=${ProgramID.AUPROFESSIONALDEV}`
      );
    } else {
      history.push(
        `/program/professionaldevelopment?pid=${ProgramID.PROFESSIONALDEV}`
      );
    }
  }, [history, isAussie]);

  const openCaregiverIntroduction = useCallback(() => {
    setShowMenuView(false);
    if (isAussie) {
      window.open(
        "https://drive.google.com/file/d/1n4YIl1u6ni1BMjp6iQhBZJBWiegwUJC1/view?usp=drive_link",
        "_blank"
      );
    } else {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Open+Parachute+Wellbeing+Resources+for+Caregivers+US.pdf",
        "_blank"
      );
    }
  }, [isAussie]);

  const openCaregiverResources = useCallback(() => {
    setShowMenuView(false);
    history.push("/caregiver");
  }, [history]);

  const openHelpCenter = useCallback(() => {
    setShowMenuView(false);
    window.open(
      "https://help.openparachuteschools.com",
      "_blank"
    );
  }, []);

  const handleScheduleSupportCall = useCallback(() => {
    setShowMenuView(false);
    if (openScheduleSupportCall) {
      openScheduleSupportCall();
    } else {
      window.open(
        "https://openparachute.typeform.com/to/nftVJf4A",
        "_blank"
      );
    }
  }, [openScheduleSupportCall]);

  const openViewActiveLessons = useCallback(() => {
    setShowMenuView(false);
    history.push("/lessonprogress");
  }, [history]);

  const openMyProfile = useCallback(() => {
    setShowMenuView(false);
    history.push("/profile");
  }, [history]);

  const sideMenuItems: MenuProps["items"] = useMemo(() => {
    return [
      {
        key: '1',
        label: (
          <MenuLink
            iconUrl={IconDashboard}
            title="My Dashboard"
            onClick={openDashboard}
            isSubMenu={false}
          />)
      },
      {
        key: '2',
        label: (
          <MenuLink
            iconUrl={IconGettingStarted}
            title="Getting Started"
          />
        ),
        children: [
          {
            key: '21',
            label: (
              <SubMenuLink
                iconUrl={IconEducatorOnboarding}
                title="Educator Onboarding"
                onClick={openEducatorOnboarding}
                tooltipTitle="Visit our Help Centre to review our Onboarding documentation. These informative guides are designed to get you up and running quickly!"
                tooltipContent="If you would like to dive deeper, there are plenty of in-depth resources available too!"
              />)
          },
          ...(isAdmin ? [{
            key: '22',
            label: (
              <SubMenuLink
                iconUrl={IconSchoolOnboarding}
                title="School Onboarding"
                onClick={openSchoolOnboarding}
                tooltipTitle="Access a plan for implementing Open Parachute in your school."
              />)
          }] : []),
          ...(isDistrictAdmin || isSuperAdmin ? [{
            key: '23',
            label: (
              <SubMenuLink
                iconUrl={IconDistrictOnboarding}
                title="District Onboarding"
                onClick={openDistrictOnboarding}
                tooltipTitle="Access resources and materials for implementing Open Parachute across your district."
              />)
          }] : []),
          {
            key: '24',
            label: (
              <SubMenuLink
                iconUrl={IconQuickstartArticles}
                title="Quickstart Articles"
                onClick={openQuickstartArticles}
                tooltipTitle="Access a comprehensive collection of Quickstart articles designed to guide you through the platform's key features and functionalities."
                tooltipContent="These articles are organized into categories to help you find the information you need quickly!"
              />)
          },
          ...(!isAussie ? [{
            key: '25',
            label: (
              <SubMenuLink
                iconUrl={IconScopeSequence}
                title="Curriculum Overviews"
                onClick={openScopeSequence}
                tooltipTitle="Explore our comprehensive Curriculum Overviews documentation, designed to help you understand the curriculum and lesson structure of Open Parachute."
                tooltipContent="This guide provides detailed information about the curriculum overviews of the curriculum, including the key concepts and skills covered in each lesson."
              />)
          }] : []),
          ...(isAussie ? [{
            key: '26',
            label: (
              <SubMenuLink
                iconUrl={IconScopeSequence}
                title="Scope & Sequence"
                onClick={openScopeSequence}
                tooltipTitle="View the Scope & Sequence for each Grade."
              />)
          }] : []),
          ...(isAussie ? [{
            key: '27',
            label: (
              <SubMenuLink
                iconUrl={IconCurriculumMapping}
                title="Curriculum Mapping"
                onClick={openCurriculumMapping}
                tooltipTitle="Explore our comprehensive Curriculum Mapping documentation, designed to help you understand the curriculum and lesson structure of Open Parachute."
                tooltipContent="This guide provides detailed information about the curriculum mapping of the curriculum, including the key concepts and skills covered in each lesson."
              />)
          }] : []),
          {
            key: '28',
            label: (
              <SubMenuLink
                iconUrl={IconLessonRoadmaps}
                title="Lesson Roadmaps"
                onClick={openLessonRoadmaps}
                tooltipTitle="Lesson Roadmaps provide lesson recommendations for every grade to help you get started quickly."
                tooltipContent="Select the best path based on the needs and comfort level of your class!"
              />)
          },
          ...(isAussie && isAdmin ? [{
            key: '29',
            label: (
              <SubMenuLink
                iconUrl={IconKickOffCall}
                title="Schedule Kick-Off Call"
                onClick={openScheduleKickOffCall}
                tooltipTitle="Book in a Video Conference call with our Team to assist with Implementing Open Parachute at your school."
                tooltipContent="Ensure the key people who oversee Wellbeing at your school can attend this call."
              />)
          }] : []),
        ]
      },
      ...(isAdmin ? [{
        key: '3',
        label: (
          <MenuLink
            iconUrl={IconAdminTop}
            title="User Management"
          />
        ),
        children: [
          {
            key: '31',
            label: (
              <SubMenuLink
                iconUrl={IconUserManage}
                title="User Management"
                onClick={openUserManagement}
                tooltipTitle="Manage all your staff. Reset Passwords, check their usage, delete accounts."
                tooltipContent="See the last time a staff member logged in, who hasn't accepted their invite, and what Lessons they have run!"
              />)
          },
          {
            key: '32',
            label: (
              <SubMenuLink
                iconUrl={IconInviteStaff}
                title="Invite Staff"
                onClick={inviteUserHandler}
                tooltipTitle="Click here to open the Invite Panel, where you can invite staff individually or in bulk."
                tooltipContent="There’s 3 ways to get staff access; Individual Invites; Bulk Invites; and a Registration Link"
              />)
          },
          {
            key: '33',
            label: (
              <SubMenuLink
                iconUrl={IconViewUsage}
                title="Usage Insights"
                onClick={openUsagePage}
                tooltipTitle="View all your staff's usage in one place. Track which staff are most active, which grade levels have the highest use, and how many lessons have been completed!"
              />)
          },
          ...(isSuperAdmin ? [{
            key: '34',
            label: (
              <SubMenuLink
                iconUrl={IconAccounts}
                title="Accounts"
                onClick={openAccountsPage}
                tooltipTitle="View all your staff's usage in one place. Track which staff are most active, which grade levels have the highest use, and how many lessons have been completed!"
              />)
          }] : []),
        ]
      }] : []),
      ...(isSuperAdmin ? [{
        key: '4',
        label: (
          <MenuLink
            iconUrl={IconUsers}
            title="Super Admin Tools"
          />
        ),
        children: [
          {
            key: '41',
            label: (
              <SubMenuLink
                iconUrl={IconReportTop}
                title="Report Management"
                to="/super/report-management"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          },
          {
            key: '42',
            label: (
              <SubMenuLink
                iconUrl={IconSentryTracking}
                title="Sentry Tracking"
                to="/super/sentry"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          },
          {
            key: '43',
            label: (
              <SubMenuLink
                iconUrl={IconCourses}
                title="Courses"
                to="/super/courses"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          },
          {
            key: '44',
            label: (
              <SubMenuLink
                iconUrl={IconUnits}
                title="Units"
                to="/super/units"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          },
          {
            key: '45',
            label: (
              <SubMenuLink
                iconUrl={IconLessons}
                title="Lessons"
                to="/super/lessons"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          },
          {
            key: '46',
            label: (
              <SubMenuLink
                iconUrl={IconGroups}
                title="Groups"
                to="/super/groups"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          },
          {
            key: '47',
            label: (
              <SubMenuLink
                iconUrl={IconGroups}
                title="Collections"
                to="/super/collections"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          },
          {
            key: '48',
            label: (
              <SubMenuLink
                iconUrl={IconGroups}
                title="Multi Collections"
                to="/super/multi-collections"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          },
          {
            key: '49',
            label: (
              <SubMenuLink
                iconUrl={IconMyCollection}
                title="Programs"
                to="/super/program-management"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          }
        ]
      }] : []),
      ...(isContentAdmin ? [{
        key: '5',
        label: (
          <MenuLink
            iconUrl={IconUsers}
            title="Content Admin Tools"
          />
        ),
        children: [
          {
            key: '51',
            label: (
              <SubMenuLink
                iconUrl={IconUnits}
                title="Units"
                to="/super/units"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          },
          {
            key: '52',
            label: (
              <SubMenuLink
                iconUrl={IconLessons}
                title="Lessons"
                to="/super/lessons"
                onClick={() => { setShowMenuView(false); }}
              />
            )
          }
        ] 
      }] : []),
      ...(isSuperAdmin ? [{
        key: '6',
        label: (
          <MenuLink
            iconUrl={IconUsers}
            title="Home Ed Tools"
          />
        ),
        children: [
          {
            key: '61',
            label: (
              <SubMenuLink
                iconUrl={IconHeUsers}
                to="/super/heusers"
                onClick={() => { setShowMenuView(false); }}
                title="Users"
              />
            )
          },
          {
            key: '62',
            label: (
              <SubMenuLink
                iconUrl={IconHeLinks}
                to="/super/helinks"
                onClick={() => { setShowMenuView(false); }}
                title="Links"
              />
            )
          }
        ]
      }] : []),
      {
        key: '7',
        label: (
          <MenuLink
            iconUrl={IconWellbeingFoundation}
            title={isAussie ? "Wellbeing Foundations" : "Well-being Foundations"}
          />
        ),
        children: [
          {
            key: '71',
            label: (
              <SubMenuLink
                iconUrl={IconStudentIntroduction}
                title="Student Introduction"
                onClick={openStudentIntroduction}
                tooltipTitle="Start here when first introducing Open Parachute to your class."
                tooltipContent="Run a few Brain Breaks prior to introducing the lessons to help your class feel more comfortable to dive deeper into the lessons!"
              />)
          },
          {
            key: '72',
            label: (
              <SubMenuLink
                iconUrl={IconBrainBreak}
                title="Brain Breaks"
                onClick={openBrainBreak}
                tooltipTitle="Brain breaks are fun & short classroom icebreakers or skill-building activities, ranging from 5-15 minutes."
              />)
          },
          {
            key: '73',
            label: (
              <SubMenuLink
                iconUrl={IconSkillBuilder}
                title="Skill-Builders"
                onClick={openSkillBuilder}
                tooltipTitle="Short, approachable/practical lessons intended to practice well-being strategies anytime in any course. These will build on skills that students have already practiced as well as provide the opportunity to learn and practice new skills."
              />)
          }
        ]
      },
      {
        key: '8',
        label: (
          <MenuLink
            iconUrl={IconLessons}
            title="Classroom Lessons"
          />
        ),
        children: [
          ...(isAussie ? [{
            key: '81',
            label: (
              <SubMenuLink
                iconUrl={IconStandalone}
                title="Standalone Lessons"
                onClick={openLessonLibrary}
                tooltipTitle="This is the hub for all lessons available on Open Parachute. Lessons are organized by grade and further categorized by theme. Search for lessons based on grade, topic, lesson type, tier and competency."
              />
            )
          }] : []),
          ...(!isAussie ? [{
            key: '82',
            label: (
              <SubMenuLink
                iconUrl={IconLessonLibrary}
                title="Lesson Library"
                onClick={openLessonLibrary}
                tooltipTitle="This is the hub for all lessons available on Open Parachute. Lessons are organized by grade and further categorized by theme. Search for lessons based on grade, topic, lesson type, tier and competency."
              />
            )
          }] : []),
          {
            key: '83',
            label: (
              <SubMenuLink
                iconUrl={IconPrograms}
                title="Programs"
                onClick={openPrograms}
                tooltipTitle="Programs contain sequenced lessons that are designed to be completed in series and allow for more depth and understanding into a particular topic area."
              />
            )
          }
        ]
      },
      {
        key: '9',
        label: (
          <MenuLink
            iconUrl={IconAssessmentReports}
            title="Assessments & Reports"
          />
        ),
        children: [
          {
            key: '91',
            label: (
              <SubMenuLink
                iconUrl={IconPreProgramQuiz}
                title="Pre-Program Quiz"
                onClick={openPreProgramQuiz}
                tooltipTitle="This online quiz will provide you with an indication of your students’ competencies, which can help guide your lesson selections."
                tooltipContent="Students require a device to complete the Quiz. It will take approximately 15 minutes."
              />
            )
          },
          ...(!isAUuser ? [{
            key: '92',
            label: (
              <SubMenuLink
                iconUrl={IconPreProgramQuiz}
                title="Post-Program Quiz"
                onClick={openPostProgramQuiz}
                tooltipTitle="This online quiz will provide you with an indication of your students’ competencies, which can help guide your lesson selections."
                tooltipContent="Students require a device to complete the Quiz. It will take approximately 15 minutes."
              />
            )
          }] : []),
          {
            key: '93',
            label: (
              <SubMenuLink
                iconUrl={IconSummaryReports}
                title="Summary Reports"
                onClick={openExitTicketSummary}
                tooltipTitle="Export Summary Reports for the Pre-Program Quiz, Exit Tickets, and Assessments."
                tooltipContent="You can filter by date to select a specific period of time!"
              />
            )
          },
          ...(isAussie ? [{
            key: '94',
            label: (
              <SubMenuLink
                iconUrl={IconAssessmentReports}
                title="Assessments & \nStudy Guides"
                onClick={openAssessmentsStudyguides}
                tooltipTitle="Create your own Assessments based on the specific units your students have completed during the year."
                tooltipContent="It’s important to complete the Unit Study Guides, as the Assessments test knowledge specifically relating to the Units!"
              />
            )
          }] : []),
          ...(isAussie && isAdmin ? [{
            key: '95',
            label: (
              <SubMenuLink
                iconUrl={IconStudentWellbeing}
                title="Student Wellbeing \nInsight Survey"
                onClick={openStudentWellBeingUnit}
                tooltipTitle="The Student Wellbeing survey provides anonymous self-report information about your students in Grades 4+"
                tooltipContent="Only Account Administrators can access and generate this survey."
              />
            )
          }] : []),
        ]
      },
      {
        key: '10',
        label: (
          <MenuLink
            iconUrl={IconProfessionalDevelopment}
            title="Professional Development"
            onClick={openProfessionalDevelopment}
            isSubMenu={false}
          />
        )
      },
      {
        key: '11',
        label: (
          <MenuLink
            iconUrl={IconParentsCaregivers}
            title="Parents & Caregivers"
          />
        ),
        children: [
          {
            key: '111',
            label: (
              <SubMenuLink
                iconUrl={IconCaregiverIntroduction}
                title="Caregiver Introduction"
                onClick={openCaregiverIntroduction}
                tooltipTitle="Use this guide to introduce Open Parachute to your Caregiver community."
                tooltipContent="Caregivers can access online resources via their own Portal. The link is found in this PDF!"
              />
            )
          },
          {
            key: '112',
            label: (
              <SubMenuLink
                iconUrl={IconCaregiverResources}
                title="Caregiver Resources"
                onClick={openCaregiverResources}
                tooltipTitle="Resources for Caregivers to help support the wellbeing of their children at home."
                tooltipContent="Caregivers can access these resources using their own Portal. Use the Share button to generate a link!"
              />
            )
          }
        ]
      },
      {
        key: '12',
        label: (
          <MenuLink
            iconUrl={IconSupport}
            title="Support"
          />
        ),
        children: [
          {
            key: '121',
            label: (
              <SubMenuLink
                iconUrl={IconSupport}
                title="Help Center"
                onClick={openHelpCenter}
                tooltipTitle="Access further help and resources for getting Started."
                tooltipContent="The Help Centre also features guides and videos for how to support struggling students."
              />
            )
          },
          {
            key: '122',
            label: (
              <SubMenuLink
                iconUrl={IconLiveChat}
                title="Live Chat"
                onClick={openIntercom}
                tooltipTitle="Chat with a member of the Open Parachute team!"
                tooltipContent="Ask questions about the content, getting started, or any technical issues."
              />
            )
          },
          ...(!isAussie && isAdmin ? [{
            key: '123',
            label: (
              <SubMenuLink
                iconUrl={IconKickOffCall}
                title="Schedule Support Call"
                onClick={handleScheduleSupportCall}
                tooltipTitle="Book in a Video Conference call with our Team to assist with Implementing Open Parachute at your school."
              />
            )
          }] : []),
        ]
      },
      {
        key: '13',
        label: (
          <MenuLink
            iconUrl={IconMyAccount}
            title="My Account"
          />
        ),
        children: [
          {
            key: '131',
            label: (
              <SubMenuLink
                iconUrl={IconViewActiveLessons}
                title="View Active Lessons"
                onClick={openViewActiveLessons}
                tooltipTitle="View all the lessons you have begun and completed."
                tooltipContent="Reset your Lesson Progress Bars at the beginning of each new School Year!"
              />
            )
          },
          {
            key: '132',
            label: (
              <SubMenuLink
                iconUrl={IconMyAccount}
                title="My Profile"
                onClick={openMyProfile}
                tooltipTitle=""
              />
            )
          },
          {
            key: '133',
            label: (
              <SubMenuLink
                iconUrl={IconLogout}
                title="Logout"
                onClick={logout}
                tooltipTitle=""
              />
            )
          }
        ]
      }
    ];
  }, [isAUuser, isAdmin, isSuperAdmin, isDistrictAdmin]);


  return (
    <>
      {showCloseButton && (
        <div className={styles.menuLogo}>
          <Button
            label=""
            className={styles.menuButton}
            leftIcon={<CloseIcon className={styles.closeMenu} />}
            onClick={onCloseMenu}
          />
          <div className={styles.logo}>
            <Logo isParentApp={isParentAppCheck} />
          </div>
        </div>
      )}
      {/* {!showCloseButton && (
        <div className={styles.menuLogo}>
          <div className={styles.logo}>
            <Logo isParentApp={isParentAppCheck} />
          </div>
        </div>
      )} */}

      <div id="menuList" className={styles.menuList}>
        <Menu
          mode="inline"
          items={sideMenuItems}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          expandIcon={({ isOpen }) =>
            isOpen ? <IconArrowUp /> : <IconArrowDown />
          }
        />

        <FooterMenuLink
          title="Privacy Policy"
          href="https://openparachuteschools.com/privacy-policy/"
          target="_blank"
        />

        <FooterMenuLink
          title="Terms of Use"
          href="https://openparachuteschools.com/terms-of-use-app/"
          target="_blank"
        />

        <FooterMenuLink
          title="Status"
          href="http://openparachute-status.com"
          target="_blank"
          statusDescription="All Systems Operational"
        />

      </div>
    </>
  );
}

export default MenuContent;
